import * as THREE from 'three';
import SceneManager from '../../../core/SceneManager';
import { Orb } from '../../../orbs/Orb';
import { OrbManager } from '../../../orbs/OrbManager';
import { computeRingLayout } from '../../../utils/RingLayout';
const DEFAULT_RADIUS = 6000;
function toNode(value, key, depth) {
    if (value !== null && typeof value === 'object') {
        const entries = Array.isArray(value)
            ? value.map((v, idx) => toNode(v, `${key}[${idx}]`, depth + 1))
            : Object.entries(value).map(([k, v]) => toNode(v, k, depth + 1));
        return { key, value, children: entries, depth };
    }
    return { key, value, children: [], depth };
}
function computeChildRadius(parentRadius, childCount) {
    if (childCount === 0)
        return parentRadius * 0.2;
    const spread = parentRadius / Math.max(childCount + 4, 6);
    return Math.max(parentRadius * 0.12, Math.min(spread, parentRadius * 0.35));
}
function colorForDepth(depth) {
    const palette = [
        new THREE.Color('#4f46e5'),
        new THREE.Color('#22d3ee'),
        new THREE.Color('#a855f7'),
        new THREE.Color('#f97316'),
        new THREE.Color('#22c55e'),
        new THREE.Color('#fde047'),
    ];
    return palette[depth % palette.length].clone();
}
function createLabel(text, radius) {
    const canvas = document.createElement('canvas');
    const size = 512;
    canvas.width = size;
    canvas.height = size;
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, size, size);
    ctx.fillStyle = 'rgba(15,15,20,0.8)';
    ctx.beginPath();
    ctx.arc(size / 2, size / 2, size / 2, 0, Math.PI * 2);
    ctx.fill();
    ctx.fillStyle = '#ffffff';
    ctx.font = 'bold 48px "Open Sans", sans-serif';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(text, size / 2, size / 2);
    const texture = new THREE.CanvasTexture(canvas);
    const material = new THREE.SpriteMaterial({ map: texture, transparent: true });
    const sprite = new THREE.Sprite(material);
    const scale = Math.min(radius * 0.6, 6000);
    sprite.scale.set(scale, scale, 1);
    return sprite;
}
function dataPayloadFor(node) {
    const value = node.value;
    if (!node.key) {
        if (value && typeof value === 'object') {
            return { value };
        }
        return { value };
    }
    const key = node.key.trim();
    return { [key || 'value']: value };
}
function createOrbForNode(node, radius) {
    const manager = OrbManager.getInstance();
    const color = colorForDepth(node.depth).getHex();
    const payload = dataPayloadFor(node);
    const orb = new Orb({
        orbType: 'JSON_DATA_ORB',
        radius,
        textColor: 0xf4f7ff,
        textOutlineColor: 0x10132a,
        defaultLayerScaleFactor: 0,
        layers: [
            { shape: 'sphere', color, side: THREE.BackSide },
        ],
    }, payload, node.key.trim() || '');
    manager.loadOrb(orb);
    const group = orb.group;
    group.userData.__jsonDiagramOrb = true;
    group.userData.__jsonDiagramOrbUuid = group.uuid;
    group.name = (node.key.trim() || 'json-node');
    if (orb.labelText) {
        const text = createLabel(orb.labelText, radius * 0.6);
        text.position.set(0, 0, radius * 0.05);
        group.add(text);
    }
    return { group, orb };
}
function layoutNode(node, radius) {
    const nodeGroup = new THREE.Group();
    nodeGroup.name = node.key || 'json-root';
    nodeGroup.userData.__jsonDiagramNode = true;
    const { group: orbGroup } = createOrbForNode(node, radius);
    nodeGroup.add(orbGroup);
    if (node.children.length === 0) {
        return nodeGroup;
    }
    const childRadius = computeChildRadius(radius, node.children.length);
    const items = node.children.map((child, index) => ({
        id: `${node.key || 'node'}-${index}`,
        radius: childRadius,
    }));
    const layout = computeRingLayout(items, {
        centre: new THREE.Vector3(),
        minRadius: Math.max(childRadius * 4.5, radius * 0.65),
        minSeparation: childRadius * 1.4,
        startAngle: Math.PI * 0.5,
    });
    node.children.forEach((child, index) => {
        const childGroup = layoutNode(child, childRadius);
        const placement = layout.placements[index];
        childGroup.position.copy(placement.position);
        nodeGroup.add(childGroup);
    });
    return nodeGroup;
}
function disposeExistingDiagram(root) {
    if (!root)
        return;
    const manager = OrbManager.getInstance();
    const toRemove = new Set();
    root.traverse(obj => {
        var _a;
        const uuid = (_a = obj.userData) === null || _a === void 0 ? void 0 : _a.__jsonDiagramOrbUuid;
        if (typeof uuid === 'string') {
            toRemove.add(uuid);
        }
    });
    toRemove.forEach(uuid => manager.hardRemoveOrb(uuid));
}
export function buildWorldFromOrb(orb, sceneName, targetScene) {
    var _a, _b, _c, _d, _e, _f, _g;
    const root = toNode((_a = orb.jsonData) !== null && _a !== void 0 ? _a : {}, orb.labelText || orb.orbType || ((_b = orb.group) === null || _b === void 0 ? void 0 : _b.uuid) || 'orb', 0);
    const targetName = sceneName !== null && sceneName !== void 0 ? sceneName : `orbWorld-${(_e = (_d = (_c = orb.group) === null || _c === void 0 ? void 0 : _c.uuid) === null || _d === void 0 ? void 0 : _d.slice(0, 8)) !== null && _e !== void 0 ? _e : Date.now().toString(36)}`;
    const sceneManager = SceneManager.getInstance();
    const scene = targetScene !== null && targetScene !== void 0 ? targetScene : (sceneManager.hasScene(targetName)
        ? sceneManager.getScene(targetName)
        : sceneManager.createScene(targetName));
    const holderId = `__jsonDiagram-${(_g = (_f = orb.group) === null || _f === void 0 ? void 0 : _f.uuid) !== null && _g !== void 0 ? _g : targetName}`;
    const existing = scene.getObjectByName(holderId);
    if (existing) {
        disposeExistingDiagram(existing);
        scene.remove(existing);
    }
    const baseGroup = layoutNode(root, DEFAULT_RADIUS);
    baseGroup.name = holderId;
    scene.add(baseGroup);
    return { sceneName: targetName, scene };
}
